import React, { useEffect, useState } from 'react';
import { getUserCases, getAppUser, getCookie } from '../../functions/utils';
import {
  getBestandImports,
  getBestandFiles,
  createBestandImport,
  deleteBestandImport,
  updateBestandImportStamp,
  handleBestandImport,
  resetBestandStatus,
} from '../../functions/bestand';
import '../../css/ImportComponent.css';
import ImportHeaderComponent from '../page/ImportHeaderComponent';
import DropdownComponent from '../utilities/DropdownComponent';
import LoaderComponent from '../utilities/LoaderComponent';
import FileDropComponent from '../utilities/FileDropComponent';
import ConfirmationComponent from '../utilities/ConfirmationComponent';
import BestandLogo from '../../images/BestandLogo.png';
import { useTranslations } from '../utilities/TranslationContext';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faChevronDown, faArrowLeft, faCircleCheck, faCircleXmark, faEllipsisV, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faChevronDown, faXmark, faArrowLeft, faCircleCheck, faCircleXmark, faEllipsisV, faTriangleExclamation);

const BestandComponent = () => {
  const { getTranslation, language } = useTranslations();

  const [userCases, setUserCases] = useState([]);
  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create form
  const [showEditor, setShowEditor] = useState(false);
  const [selectedCase, setSelectedCase] = useState(false);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [jrnid, setJrnid] = useState('');
  const [accid, setAccid] = useState('');

  // Per-config upload state: { configId, files, isLoading, results }
  const [activeUpload, setActiveUpload] = useState(null);

  // Confirm delete
  const [confirmDeleteId, setConfirmDeleteId] = useState(false);


  // Previously uploaded files per config (keyed by config id)
  const [configFiles, setConfigFiles] = useState({});

  useEffect(() => { init(); }, []);

  async function init() {
    const appUser = await getAppUser();
    if (!appUser) return setTimeout(init, 300);
    var cases = await getUserCases();
    if (cases) setUserCases(cases);
    var impBestanden = await getBestandImports(cases);
    console.log(impBestanden);
    setImports(Array.isArray(impBestanden) ? impBestanden : []);
    setIsLoading(false);
  }

  async function reload() {
    const imps = await getBestandImports(userCases);
    setImports(Array.isArray(imps) ? imps : []);
  }

  // ── Create ──────────────────────────────────────────────────────────────────

  function toggleEditor() {
    setShowEditor(v => !v);
    setSelectedCase(false);
    setSelectedYear(String(new Date().getFullYear()));
    setJrnid('');
    setAccid('');
  }

  async function handleCreate() {
    if (!selectedCase || !selectedYear || !jrnid.trim() || !accid.trim()) {
      alert('Zorg dat alle velden gevuld zijn!');
      return;
    }
    const entiteit = `${selectedCase}_1`;
    const exists = imports.some(i => i.entiteit === entiteit && i.year === selectedYear && i.jrnid === jrnid && i.accid === accid);
    if (exists) { alert('Deze configuratie bestaat al!'); return; }

    await createBestandImport({ entiteit, year: selectedYear, jrnid: jrnid.trim(), accid: accid.trim() });
    toggleEditor();
    await reload();
  }

  // ── Upload ───────────────────────────────────────────────────────────────────

  async function openUpload(config) {
    setActiveUpload({ config, files: [], isLoading: false, results: [] });
    if (config.impid > 0 && !configFiles[config.id]) {
      const files = await getBestandFiles(config.id);
      setConfigFiles(prev => ({ ...prev, [config.id]: files }));
    }
  }

  function closeUpload() {
    setActiveUpload(null);
  }

  async function runImport() {
    const { config, files } = activeUpload;
    setActiveUpload(u => ({ ...u, isLoading: true, results: [] }));
    await resetBestandStatus();

    const newResults = [];
    for (const file of files) {
      let xmlContent;
      try { xmlContent = await file.text(); }
      catch { newResults.push({ file: file.name, success: false, error: 'Kan bestand niet lezen' }); continue; }

      try {
        const result = await handleBestandImport({
          entiteit: config.entiteit,
          year: config.year,
          jrnid: config.jrnid,
          accid: config.accid,
          fileName: file.name,
          xmlContent,
        });
        newResults.push({ file: file.name, success: true, ...result });
      } catch (e) {
        newResults.push({ file: file.name, success: false, error: e.message });
      }
    }

    const anySuccess = newResults.some(r => r.success);
    if (anySuccess) await updateBestandImportStamp(config.id);

    setActiveUpload(u => ({ ...u, isLoading: false, results: newResults, files: [] }));
    await reload();

    if (anySuccess) {
      const freshImps = await getBestandImports(userCases);
      const freshConfig = freshImps.find(i => i.id === config.id);
      if (freshConfig) await refreshConfigFiles(freshConfig);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    await deleteBestandImport(id);
    setConfirmDeleteId(false);
    await reload();
  }

  // ── Config file history ───────────────────────────────────────────────────────

  async function refreshConfigFiles(imp) {
    if (imp.impid > 0) {
      const files = await getBestandFiles(imp.id);
      setConfigFiles(prev => ({ ...prev, [imp.id]: files }));
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function getCaseName(entiteit) {
    const id = entiteit.substring(0, entiteit.indexOf('_'));
    const c = userCases.find(uc => String(uc.id) === String(id));
    return c ? c.name : entiteit;
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function removePendingFile(index) {
    setActiveUpload(u => {
      const files = u.files.filter((_, i) => i !== index);
      return { ...u, files };
    });
  }

  function formatStamp(stamp) {
    if (!stamp) return '-';
    return new Date(stamp).toLocaleString(`${language?.language_code}-${language?.country_code}`, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
  }

  const canCreate = selectedCase && selectedYear && jrnid.trim() && accid.trim();

  return (
    <div className="import-container">
      <ImportHeaderComponent
        title="Bestand"
        logo={BestandLogo}
        subtitle={getTranslation('import_BestandSubtitle')}
      />
      <div className="import-component-container">
        <div className="import-component-content">
          <div className="import-content">

            {/* ── Create form ── */}
            {showEditor && (
              <div className="import-create-basic">
                <div className="case-content">
                  <p>{getTranslation('import_FVCase')}*</p>
                  <DropdownComponent
                    options={userCases.map(c => ({ label: c.name, value: c.id, type: 'case' }))}
                    defaultValue={{ label: getTranslation('import_SelectCase'), value: null, type: null }}
                    onSelect={o => o.type === 'case' && setSelectedCase(o.value)}
                    className="case"
                  />
                </div>
                <div className="jaar-content">
                  <p>{getTranslation('import_DropdownYear')}*</p>
                  <input
                    type="number"
                    min="1000"
                    max="9999"
                    className="jaar-input"
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                  />
                </div>
                <div className="administration-content">
                  <p>Dagboek (jrnid)*</p>
                  <input
                    type="text"
                    className="jaar-input"
                    placeholder="bijv. BANK"
                    value={jrnid}
                    onChange={e => setJrnid(e.target.value)}
                  />
                </div>
                <div className="administration-content">
                  <p>Bankrekening (accid)*</p>
                  <input
                    type="text"
                    className="jaar-input"
                    placeholder="bijv. 1010"
                    value={accid}
                    onChange={e => setAccid(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* ── Menubar ── */}
            <div className="import-menubar">
              {!showEditor ? (
                <button className="import-btn new-btn hover" onClick={toggleEditor}>
                  {getTranslation('import_CreateNew')}
                </button>
              ) : (
                <>
                  <button className="import-btn submit-btn hover" disabled={!canCreate} onClick={handleCreate}>
                    {getTranslation('import_Submit')}
                  </button>
                  <button className="import-btn cancel-btn hover" onClick={toggleEditor}>
                    {getTranslation('import_Cancel')}
                  </button>
                </>
              )}
            </div>

            {/* ── Config list ── */}
            <LoaderComponent isLoading={isLoading} paddingTop="20px" />
            {!isLoading && imports.length > 0 && (
              <div className="list-content">
                <table border="1">
                  <tbody>
                    <tr className="list-header">
                      <td className="list-expand"></td>
                      <td className="list-case">{getTranslation('import_Case')}</td>
                      <td className="list-year">{getTranslation('import_Year')}</td>
                      <td>Dagboek</td>
                      <td>Bankrekening</td>
                      <td className="list-stamp no-wrap">{getTranslation('import_LastUpdated')}</td>
                      <td className="list-update no-wrap">{getTranslation('import_Import')}</td>
                      <td className="edit"></td>
                    </tr>
                    {imports.map((imp, index) => {
                      const isUploadActive = activeUpload?.config.id === imp.id;
                      const canImportThis = isUploadActive && activeUpload.files.length > 0 && !activeUpload.isLoading;
                      return (
                        <React.Fragment key={imp.id}>
                          <tr value={index} onClick={() => isUploadActive ? closeUpload() : openUpload(imp)}>
                            <td className="td-expand">
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`config-expand-icon${isUploadActive ? ' rotated' : ''}`}
                              />
                            </td>
                            <td>{getCaseName(imp.entiteit)}</td>
                            <td>{imp.year}</td>
                            <td>{imp.jrnid}</td>
                            <td>{imp.accid}</td>
                            <td>{formatStamp(imp.stamp)}</td>
                            <td className="td-update">
                              <button className={`btn-list hover${canImportThis ? '' : ' disabled'}`} disabled={!canImportThis} onClick={e => { e.stopPropagation(); runImport(); }}>
                                {getTranslation('import_Import')}
                              </button>
                            </td>
                            <td className="td-editor">
                              <div className="delete-list list-editor-option hover" onClick={e => { e.stopPropagation(); setConfirmDeleteId(imp.id); }}>
                                <FontAwesomeIcon icon={faXmark} />
                              </div>
                              {confirmDeleteId === imp.id && (
                                <ConfirmationComponent
                                  message="Weet u zeker dat u deze configuratie wilt verwijderen?"
                                  onCancel={() => setConfirmDeleteId(false)}
                                  onConfirm={() => handleDelete(imp.id)}
                                />
                              )}
                            </td>
                          </tr>

                          {isUploadActive && (
                            <tr className="config-files-row">
                              <td colSpan={8}>
                                <LoaderComponent isLoading={activeUpload.isLoading} paddingTop="10px" />
                                {!activeUpload.isLoading && (
                                  <>
                                    <FileDropComponent
                                      onFilesAccepted={files => setActiveUpload(u => ({ ...u, files }))}
                                      showFileList={false}
                                    />
                                    {(() => {
                                      const existing = configFiles[activeUpload.config.id] ?? [];
                                      const selected = activeUpload.files;
                                      const failures = activeUpload.results.filter(r => !r.success);
                                      if (existing.length === 0 && selected.length === 0 && failures.length === 0) return null;
                                      return (
                                        <div className="import-results-wrapper">
                                          <table className="import-results-table">
                                            <thead>
                                              <tr>
                                                <th></th>
                                                <th>Bestandsnaam</th>
                                                <th>Type</th>
                                                <th>Periode</th>
                                                <th>Transacties / Grootte</th>
                                                <th></th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {selected.map((f, i) => {
                                                const isDuplicate = existing.some(e => e.file_name === f.name);
                                                return (
                                                  <tr key={`sel-${i}`} className={`file-row-pending${isDuplicate ? ' file-row-duplicate' : ''}`}>
                                                    <td>{isDuplicate && <FontAwesomeIcon icon={faTriangleExclamation} title="Duplicaat" />}</td>
                                                    <td>{f.name}{isDuplicate && <span className="duplicate-label">duplicaat</span>}</td>
                                                    <td>{f.name.split('.').pop().toUpperCase()}</td>
                                                    <td>-</td>
                                                    <td>{formatSize(f.size)}</td>
                                                    <td>
                                                      <button className="file-drop-item-remove" onClick={() => removePendingFile(i)} type="button">
                                                        <FontAwesomeIcon icon={faXmark} />
                                                      </button>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                              {failures.map((r, i) => (
                                                <tr key={`fail-${i}`} className="result-error">
                                                  <td><FontAwesomeIcon icon={faCircleXmark} /></td>
                                                  <td>{r.file}</td>
                                                  <td>-</td>
                                                  <td colSpan={3}>{r.error}</td>
                                                </tr>
                                              ))}
                                              {existing.map(f => (
                                                <tr key={`ex-${f.id}`} className="file-row-existing">
                                                  <td><FontAwesomeIcon icon={faCircleCheck} /></td>
                                                  <td>{f.file_name}</td>
                                                  <td>{f.file_type}</td>
                                                  <td>{f.opening_balance_date && f.closing_balance_date ? `${f.opening_balance_date} – ${f.closing_balance_date}` : '-'}</td>
                                                  <td>{f.transaction_count}</td>
                                                  <td></td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      );
                                    })()}
                                  </>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
};

export default BestandComponent;
